import { t } from "@lingui/macro";
import { ArrowRightIcon, CheckCircleIcon } from "@phosphor-icons/react";
import type { ResumeData } from "@reactive-resume/schema";
import { cn } from "@reactive-resume/utils";

import { getDetectedSections } from "../detected-sections";

const IMPORT_ASSETS_PATH = "/images/user-space/onboarding/import-document";

type Props = {
  file: File;
  data: ResumeData;
  onBack: () => void;
  onChangeFile: () => void;
  onContinue: () => void;
};

const formatSize = (bytes: number) => {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} Ko`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const getFileFormat = (file: File) => {
  const fileName = file.name.toLowerCase();

  if (
    file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    fileName.endsWith(".docx")
  ) {
    return {
      label: t`Document Word (.docx)`,
      image: `${IMPORT_ASSETS_PATH}/import-format-word.webp`,
    };
  }

  if (file.type.startsWith("image/") || /\.(jpe?g|png|webp)$/i.test(fileName)) {
    return {
      label: "Image",
      image: `${IMPORT_ASSETS_PATH}/import-format-image.webp`,
    };
  }

  return {
    label: t`Document PDF (.pdf)`,
    image: `${IMPORT_ASSETS_PATH}/import-format-pdf.webp`,
  };
};

export const DetectedSectionsStep = ({ file, data, onBack, onChangeFile, onContinue }: Props) => {
  const sections = getDetectedSections(data);

  const fileFormat = getFileFormat(file);

  return (
    <section
      className={cn(
        "mx-auto flex h-full min-h-0 w-full max-w-3xl",
        "flex-col justify-center overflow-hidden",
      )}
    >
      <header className="shrink-0">
        <h1
          className={cn(
            "text-2xl font-bold tracking-tight text-indigo-950",
            "sm:text-3xl lg:text-[34px] lg:leading-[1.15]",
          )}
        >
          Sections détectées dans votre CV
        </h1>

        <p className={cn("mt-1.5 text-xs leading-5 text-indigo-950/75", "sm:text-sm sm:leading-6")}>
          Vérifiez les informations retrouvées avant de poursuivre.
        </p>
      </header>

      <article
        className={cn(
          "mt-4 flex shrink-0 items-center gap-3",
          "rounded-2xl border border-indigo-100 bg-white",
          "px-3 py-2.5",
          "shadow-[0_5px_16px_rgba(15,23,42,0.05)]",
          "sm:mt-5 sm:gap-4 sm:px-4 sm:py-3",
        )}
      >
        <span
          className={cn(
            "grid size-12 shrink-0 place-items-center",
            "rounded-xl bg-indigo-50",
            "sm:size-14",
          )}
        >
          <img
            src={fileFormat.image}
            alt=""
            aria-hidden
            draggable={false}
            className="size-10 object-contain sm:size-12"
          />
        </span>

        <div className="min-w-0 flex-1">
          <p className={cn("truncate text-xs font-bold text-indigo-950", "sm:text-sm")}>
            {file.name}
          </p>

          <p
            className={cn(
              "mt-0.5 flex flex-wrap items-center gap-x-1.5",
              "text-[10px] text-slate-500 sm:text-[11px]",
            )}
          >
            <span>{fileFormat.label}</span>

            <span aria-hidden>•</span>

            <span>{formatSize(file.size)}</span>
          </p>
        </div>

        <div
          className={cn(
            "flex shrink-0 flex-col items-end gap-1.5",
            "sm:flex-row sm:items-center sm:gap-3",
          )}
        >
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full",
              "bg-emerald-50 px-2.5 py-1.5",
              "text-[9px] font-bold text-emerald-700",
              "sm:px-3 sm:text-[10px]",
            )}
          >
            <CheckCircleIcon
              aria-hidden
              size={15}
              weight="fill"
              className="shrink-0 text-emerald-500"
            />

            <span className="xs:inline hidden">Analyse terminée</span>

            <span className="xs:hidden">Terminé</span>
          </span>

          <button
            type="button"
            onClick={onChangeFile}
            className={cn(
              "rounded-md text-[9px] font-bold text-indigo-600",
              "transition-colors hover:text-indigo-800",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-indigo-500",
              "sm:text-[10px]",
            )}
          >
            Changer
          </button>
        </div>
      </article>

      <div
        className={cn(
          "mt-3 min-h-0 shrink rounded-2xl",
          "border border-indigo-100 bg-white",
          "p-3 shadow-[0_5px_16px_rgba(15,23,42,0.04)]",
          "sm:mt-4 sm:p-4",
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className={cn("text-sm font-bold text-indigo-950", "sm:text-base")}>
            Nous avons trouvé
          </h2>

          <span
            className={cn(
              "shrink-0 rounded-full bg-indigo-50",
              "px-2.5 py-1 text-[9px] font-bold text-indigo-700",
              "sm:text-[10px]",
            )}
          >
            {sections.length} {sections.length > 1 ? "sections" : "section"}
          </span>
        </div>

        {sections.length > 0 ? (
          <div className={cn("mt-2.5 grid grid-cols-2 gap-2", "sm:mt-3 sm:gap-2.5")}>
            {sections.map((section) => (
              <article
                key={section.key}
                className={cn(
                  "flex min-w-0 items-center gap-2",
                  "rounded-xl border border-indigo-100",
                  "bg-white px-2.5 py-2",
                  "transition-colors hover:bg-indigo-50/40",
                  "sm:gap-3 sm:px-3 sm:py-2.5",
                )}
              >
                <span
                  className={cn(
                    "grid size-7 shrink-0 place-items-center",
                    "rounded-full bg-emerald-50",
                    "sm:size-8",
                  )}
                >
                  <CheckCircleIcon
                    aria-hidden
                    size={19}
                    weight="fill"
                    className="text-emerald-500 sm:size-5"
                  />
                </span>

                <div className="min-w-0 flex-1">
                  <p className={cn("truncate text-[11px] font-bold text-indigo-950", "sm:text-xs")}>
                    {section.label}
                  </p>

                  <p className={cn("mt-0.5 truncate text-[9px] text-slate-500", "sm:text-[10px]")}>
                    {section.key === "basics"
                      ? section.description
                      : `${section.count} ${section.description}`}
                  </p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p
            className={cn(
              "mt-3 rounded-xl bg-amber-50 px-3 py-2.5",
              "text-[10px] font-medium leading-4 text-amber-800",
              "sm:text-xs",
            )}
          >
            Aucune section n’a pu être identifiée automatiquement. Vous pourrez compléter votre CV
            dans l’éditeur.
          </p>
        )}
      </div>

      <p
        className={cn(
          "mt-3 flex shrink-0 items-center gap-2",
          "rounded-xl border border-emerald-100 bg-emerald-50",
          "px-3 py-2 text-[10px] font-medium",
          "leading-4 text-emerald-800",
          "sm:px-4 sm:text-[11px]",
        )}
      >
        <CheckCircleIcon
          aria-hidden
          size={18}
          weight="fill"
          className="shrink-0 text-emerald-500"
        />

        <span>Toutes les sections trouvées seront importées et resteront modifiables.</span>
      </p>

      <footer className={cn("mt-3 flex shrink-0 items-center gap-3", "sm:mt-4")}>
        <button
          type="button"
          onClick={onBack}
          className={cn(
            "min-h-10 shrink-0 rounded-xl",
            "border-2 border-indigo-600 px-5",
            "text-xs font-bold text-indigo-700",
            "transition-colors hover:bg-indigo-50",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
            "sm:min-h-11 sm:px-6 sm:text-sm",
          )}
        >
          Retour
        </button>

        <button
          type="button"
          onClick={onContinue}
          className={cn(
            "inline-flex min-h-10 min-w-0 flex-1",
            "items-center justify-center gap-2 rounded-xl",
            "bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500",
            "px-5 text-xs font-bold text-white",
            "shadow-[0_7px_18px_rgba(79,70,229,0.2)]",
            "transition-all duration-200",
            "hover:-translate-y-0.5",
            "hover:shadow-[0_10px_22px_rgba(79,70,229,0.25)]",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
            "sm:min-h-11 sm:px-7 sm:text-sm",
          )}
        >
          Continuer
          <ArrowRightIcon aria-hidden size={18} weight="bold" className="shrink-0" />
        </button>
      </footer>
    </section>
  );
};
